import type { Variants } from 'framer-motion'
import { motion, useAnimation, useIsPresent } from 'framer-motion'
import type React from 'react'
import { useEffect, useState } from 'react'
import { Card } from '../../atoms'
import { fadeExit, flip, peel, slideDown, slideUp, toss } from './constants'

export type AnimationType =
  | 'slideUp'
  | 'throw'
  | 'flip'
  | 'peel'
  | 'slideDown'
  | 'fadeExit'
export type Direction = 'left' | 'right'

interface Props extends React.ComponentProps<typeof Card> {
  animationType: AnimationType
  direction?: Direction
  onAnimationComplete?: () => void
}

export const AnimatedCard = (props: Props) => {
  const [animationType, setAnimationType] = useState(props.animationType)
  const animation = useAnimation()
  const isAnimationPresent = useIsPresent()

  useEffect(() => {
    if (animationType === 'slideUp') {
      void animation.start('visible')
    } else if (animationType === 'flip' || animationType === 'peel') {
      void animation
        .start('back')
        .then(() => animation.start('mid'))
        .then(() => animation.start('front'))
    }
  }, [animationType, animation])

  useEffect(() => {
    if (!isAnimationPresent && animationType === 'throw') {
      void animation.start('exit')
    }
  }, [isAnimationPresent, animationType, animation])

  useEffect(() => {
    if (animationType === 'slideUp') {
      if (props.value === 'null') {
        setAnimationType('throw')
        return
      }
      setAnimationType('flip')
      return
    }
  }, [props.value])

  let initial: string
  if (animationType === 'slideUp') {
    initial = 'hidden'
  } else if (animationType === 'throw') {
    initial = 'initial'
  } else {
    initial = 'back'
  }

  const variantMap: Record<AnimationType, Variants> = {
    slideUp,
    throw: toss,
    flip,
    peel,
    slideDown,
    fadeExit,
  }

  const perspective =
    animationType === 'flip' || animationType === 'peel' ? 600 : undefined

  return (
    <motion.div
      style={{
        perspective,
        width: '100%',
      }}
      variants={variantMap[animationType]}
      initial={initial}
      animate={animation}
      exit='exit'
      custom={{ direction: props.direction ?? 'right' }}
    >
      <Card
        value={props.value}
        cardBack={props.cardBack}
        theme={props.theme}
        size={props.size}
        style={props.style}
        className={props.className}
      />
    </motion.div>
  )
}
